import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Hash, Upload, FileText, File, FileSpreadsheet, Download, Trash2, Pencil, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_ICONS = {
  pdf: { icon: FileText, color: 'text-red-400', bg: 'bg-red-900/30' },
  docx: { icon: File, color: 'text-blue-400', bg: 'bg-blue-900/30' },
  doc: { icon: File, color: 'text-blue-400', bg: 'bg-blue-900/30' },
  xlsx: { icon: FileSpreadsheet, color: 'text-green-400', bg: 'bg-green-900/30' },
  xls: { icon: FileSpreadsheet, color: 'text-green-400', bg: 'bg-green-900/30' },
  pptx: { icon: FileText, color: 'text-orange-400', bg: 'bg-orange-900/30' },
};

const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

export default function ISYBirimFiyat({ ihaleler }) {
  const [selectedIhale, setSelectedIhale] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editAciklama, setEditAciklama] = useState('');
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async (ihaleId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/isy/birim-fiyat/${ihaleId}`, { withCredentials: true });
      setItems(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedIhale) fetchData(selectedIhale.id); }, [selectedIhale, fetchData]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    let success = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/isy/birim-fiyat/${selectedIhale.id}/upload`, formData, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
        success++;
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || 'Yükleme hatası'}`);
      }
    }
    if (success > 0) {
      toast.success(`${success} dosya yüklendi`);
      fetchData(selectedIhale.id);
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/isy/birim-fiyat/item/${deleteId}`, { withCredentials: true });
      toast.success('Belge silindi'); setDeleteId(null); fetchData(selectedIhale.id);
    } catch (e) { toast.error('Silme hatası'); }
  };

  const handleSaveAciklama = async (itemId) => {
    try {
      await axios.put(`${API}/isy/birim-fiyat/item/${itemId}`, { aciklama: editAciklama }, { withCredentials: true });
      toast.success('Açıklama güncellendi');
      setEditId(null);
      fetchData(selectedIhale.id);
    } catch (e) { toast.error('Hata'); }
  };

  const getDownloadUrl = (item) => `${API}/isy/dosya/${item.dosya_yolu}`;

  if (!selectedIhale) {
    return (
      <div className="space-y-4" data-testid="isy-birim-fiyat">
        <div className="flex items-center gap-2">
          <Hash className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-bold text-white">Birim Fiyat / Pursantaj</h2>
        </div>
        <p className="text-sm text-gray-400">Belge yüklemek için bir ihale seçin:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(ihaleler || []).map(ih => (
            <Card key={ih.id} className="bg-gray-900/60 border-gray-700/50 hover:border-orange-500/40 cursor-pointer transition-colors" onClick={() => setSelectedIhale(ih)} data-testid={`bf-select-${ih.id}`}>
              <CardContent className="py-3 px-4">
                <p className="text-sm font-semibold text-white">{ih.is_adi}</p>
                <p className="text-xs text-gray-500 mt-1">IKN: {ih.ikn || '-'} | Sözleşme: {ih.sozlesme_turu || '-'}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="isy-birim-fiyat">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedIhale(null)} className="text-gray-400 hover:text-white h-8 px-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div>
            <h2 className="text-sm font-bold text-white">{selectedIhale.is_adi}</h2>
            <p className="text-xs text-gray-500">Birim Fiyat / Pursantaj Belgeleri</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-gray-800 text-gray-300 text-xs">{items.length} belge</Badge>
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx" className="hidden" onChange={handleUpload} />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} size="sm" className="bg-orange-700 hover:bg-orange-600 text-white" data-testid="bf-upload-btn">
            <Upload className="w-4 h-4 mr-1" /> {uploading ? 'Yükleniyor...' : 'Belge Yükle'}
          </Button>
        </div>
      </div>

      {/* Yükleme Alanı */}
      <Card className="bg-gray-800/30 border-gray-700/40 border-dashed border-2 hover:border-orange-600/50 transition-colors cursor-pointer" onClick={() => fileInputRef.current?.click()}>
        <CardContent className="py-8 text-center">
          <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">PDF, Word veya Excel dosyalarını buraya sürükleyin veya tıklayın</p>
          <p className="text-xs text-gray-600 mt-1">Birim Fiyat Teklif Cetveli, Pursantaj Tablosu, Analiz Dosyaları</p>
        </CardContent>
      </Card>

      {/* Belge Listesi */}
      {loading ? (
        <div className="text-center text-gray-400 py-8">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <Card className="bg-gray-900/60 border-gray-700/50">
          <CardContent className="py-12 text-center text-gray-500">
            Henüz belge yüklenmemiş. Yukarıdaki alana dosya yükleyin.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const fcfg = FILE_ICONS[item.dosya_tipi] || FILE_ICONS.pdf;
            const FIcon = fcfg.icon;
            const isEditing = editId === item.id;
            return (
              <Card key={item.id} className="bg-gray-900/60 border-gray-700/50 hover:border-gray-600/50 transition-colors" data-testid={`bf-file-${item.id}`}>
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${fcfg.bg} flex items-center justify-center flex-shrink-0`}>
                      <FIcon className={`w-5 h-5 ${fcfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{item.dosya_adi}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        <span>{formatSize(item.dosya_boyutu)}</span>
                        <Badge className="bg-gray-800 text-gray-400 text-[10px] uppercase">{item.dosya_tipi}</Badge>
                        <span>{item.yukleyen}</span>
                        <span>{item.created_at ? new Date(item.created_at).toLocaleDateString('tr-TR') : ''}</span>
                      </div>
                      {isEditing ? (
                        <div className="flex items-center gap-2 mt-1.5">
                          <Input value={editAciklama} onChange={e => setEditAciklama(e.target.value)} className="bg-gray-800 border-gray-700 text-white h-7 text-xs flex-1" placeholder="Açıklama ekle..." />
                          <Button size="sm" onClick={() => handleSaveAciklama(item.id)} className="h-7 w-7 p-0 bg-green-700 hover:bg-green-600"><Check className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="h-7 text-xs text-gray-400">İptal</Button>
                        </div>
                      ) : item.aciklama ? (
                        <p className="text-[11px] text-gray-400 mt-1 italic">{item.aciklama}</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <a href={getDownloadUrl(item)} target="_blank" rel="noopener noreferrer" download>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-orange-400" data-testid={`download-${item.id}`}><Download className="w-3.5 h-3.5" /></Button>
                      </a>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-blue-400" onClick={() => { setEditId(item.id); setEditAciklama(item.aciklama || ''); }}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-400" onClick={() => setDeleteId(item.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader><AlertDialogTitle className="text-white">Belgeyi silmek istediğinize emin misiniz?</AlertDialogTitle><AlertDialogDescription className="text-gray-400">Dosya ve kayıt kalıcı olarak silinecektir.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-700 hover:bg-red-600 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
